import { Field, Form, Formik } from "formik";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  getAdminSlide,
  updateAdminSlide,
  deleteAdminSlide,
  setAdminSlide,
} from "shared/actions/admin/adminSlideActions";
import {
  resetAction,
  runAction,
  slideActions,
} from "shared/actions/admin/adminSlidesActions";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { SlideAdminWithUrls } from "shared/reducers/admin/adminSlideReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import TextareaInput from "components/customInputs/TextareaInput";
import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import "./EditSlidePanel.css";

import SlideEditForm from "./SlideEditForm";
import HalfScreenPanel from "../editPanels/halfScreenEditPanel/HalfScreenPanel";
import WarningDialog from "../WarningDialog";

type EditSlidePanelProps = {
  slideId: number;
  returnUrl: string;
};

const EditSlidePanel = ({ slideId, returnUrl }: EditSlidePanelProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const slide = useSelector(({ admin }: ApplicationState) => admin.slide);
  const [isOpenWarningDialog, setIsOpenWarningDialog] =
    useState<boolean>(false);

  const onDeleteSlide = (values: { reason: string }) => {
    if (slide) {
      dispatch(
        deleteAdminSlide({
          slide_id: slide?.id!,
          case_id: slide?.case?.id,
          reason: values.reason,
          navigate,
          returnUrl: returnUrl,
        })
      );
    }
  };

  useEffect(() => {
    if (slideId) {
      dispatch(getAdminSlide(slideId));
    } else {
      dispatch(setAdminSlide(null));
    }
  }, [slideId]);

  useEffect(() => {
    if (!slide) {
      setIsOpenWarningDialog(false);
    }
  }, [slide]);

  const onSubmit = (slideUpdate: Partial<SlideAdminWithUrls>) => {
    dispatch(
      updateAdminSlide({
        slide: slideUpdate,
        case: slide.case as CaseAdminDetail,
        navigate,
        returnUrl: returnUrl,
      })
    );
  };

  const onClose = () => {
    navigate(returnUrl, { replace: true });

    dispatch(setAdminSlide(null));
  };

  const onResetAction = () => {
    dispatch(resetAction());
  };

  const onRunAction = (action: { slides: number[]; action: slideActions }) => {
    dispatch(runAction(action));
  };

  return (
    <>
      {isOpenWarningDialog && slide && (
        <WarningDialog
          title={`Delete ${slide.accession_number}`}
          onClose={() => {
            setIsOpenWarningDialog(false);
          }}
        >
          <Formik initialValues={{ reason: "" }} onSubmit={onDeleteSlide}>
            <Form className="EditSlideWarning">
              <Field name="reason">
                {({ field }: any) => (
                  <TextareaInput label="Reason" id="reason" {...field} />
                )}
              </Field>
              <div className="actions__container">
                <PathologyWatchButton
                  onClick={() => setIsOpenWarningDialog(false)}
                >
                  Cancel
                </PathologyWatchButton>
                <PathologyWatchButton type="submit" color="red">
                  Delete
                </PathologyWatchButton>
              </div>
            </Form>
          </Formik>
        </WarningDialog>
      )}
      <HalfScreenPanel onClose={onClose}>
        <div className="EditSlidePanel">
          {!slide || slide.isLoading ? (
            <div className="Panel__spinner">
              <Spinner />
            </div>
          ) : (
            <SlideEditForm
              adminSlide={slide}
              onCancel={onClose}
              onSubmit={onSubmit}
              onDelete={() => setIsOpenWarningDialog(true)}
              onResetAction={onResetAction}
              onRunAction={onRunAction}
            />
          )}
        </div>
      </HalfScreenPanel>
    </>
  );
};

export default EditSlidePanel;
